import React, { useState, useEffect, useRef } from 'react';
import { Card, Badge, Button, Form, Row, Col, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus,
  faSearch,
  faTimes,
  faCheck,
  faPencilAlt,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { 
  UilClock, 
  UilExclamationTriangle, 
  UilCheckCircle,
  UilEdit,
  UilTrash,
  UilListUl
} from '@iconscout/react-unicons';
import classNames from 'classnames';
import { dateFormatter, compareDateWithToday, getISODate } from 'helpers/utils';
import toast from 'react-hot-toast';
import SearchBox from 'components/common/SearchBox';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './InlineTaskList.css';

interface TaskItem {
  id: string;
  task: string;
  priority: string;
  status: string;
  due_date: string;
  is_completed: boolean;
  created_on: string;
}

interface InlineTaskListProps {
  tasks: TaskItem[];
  onCreateTask: (taskData: any) => Promise<void>;
  onUpdateTask: (taskId: string, taskData: any) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onToggleTask: (task: TaskItem) => Promise<void>;
  onSearchTask: (query: string) => void;
}

const TaskPriority = ["Low", "Medium", "High"];
const TaskStatus = ["Pending", "In Progress", "Completed"];

const InlineTaskList: React.FC<InlineTaskListProps> = ({
  tasks,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onToggleTask,
  onSearchTask
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filteredTasks, setFilteredTasks] = useState<TaskItem[]>(tasks);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form states for create
  const [newTask, setNewTask] = useState({
    task: '',
    priority: 'Medium',
    task_status: 'Pending',
    due_date: null as Date | null
  });
  
  // Form states for edit
  const [editTask, setEditTask] = useState({
    task: '',
    priority: '',
    task_status: '',
    due_date: null as Date | null
  });

  const createFormRef = useRef<HTMLDivElement>(null);
  const newTaskInputRef = useRef<HTMLInputElement>(null);

  // Filter and search tasks
  useEffect(() => {
    let filtered = [...tasks];
    
    // Apply status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'completed') {
        filtered = filtered.filter(task => task.is_completed || task.status === 'Completed');
      } else if (filterStatus === 'pending') {
        filtered = filtered.filter(task => !task.is_completed && task.status !== 'Completed');
      }
    }
    
    // Apply search filter
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(task => 
        task.task.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Sort by due date and priority
    filtered.sort((a, b) => {
      // Completed tasks go to bottom
      if (a.is_completed && !b.is_completed) return 1;
      if (!a.is_completed && b.is_completed) return -1;
      
      // Then sort by due date
      if (a.due_date && b.due_date) {
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      }
      
      return 0;
    });
    
    setFilteredTasks(filtered);
  }, [tasks, searchQuery, filterStatus]);

  // Auto-focus when showing create form
  useEffect(() => {
    if (showCreateForm && newTaskInputRef.current) {
      newTaskInputRef.current.focus();
      createFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showCreateForm]);

  const handleCreateTask = async () => {
    if (!newTask.task.trim()) {
      toast.error('Task description is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onCreateTask({
        ...newTask,
        due_date: newTask.due_date ? getISODate(newTask.due_date) : null
      });
      
      // Reset form
      setNewTask({
        task: '',
        priority: 'Medium',
        task_status: 'Pending',
        due_date: null
      });
      setShowCreateForm(false);
      toast.success('Task created successfully!');
    } catch (error) {
      toast.error('Failed to create task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTask = async (taskId: string) => {
    if (!editTask.task.trim()) {
      toast.error('Task description is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onUpdateTask(taskId, {
        ...editTask,
        due_date: editTask.due_date ? getISODate(editTask.due_date) : null
      });
      
      setEditingTaskId(null);
      toast.success('Task updated successfully!');
    } catch (error) {
      toast.error('Failed to update task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditingTask = (task: TaskItem) => {
    setEditingTaskId(task.id);
    setEditTask({
      task: task.task,
      priority: task.priority,
      task_status: task.status,
      due_date: task.due_date ? new Date(task.due_date) : null
    });
    // Cancel create form if open
    setShowCreateForm(false);
  };

  const cancelEditing = () => {
    setEditingTaskId(null);
  };

  const cancelCreate = () => {
    setShowCreateForm(false);
    setNewTask({
      task: '',
      priority: 'Medium',
      task_status: 'Pending',
      due_date: null
    });
  };

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, string> = {
      'High': 'danger',
      'Medium': 'warning',
      'Low': 'success'
    };
    return variants[priority] || 'secondary';
  };

  const getStatusIcon = (task: TaskItem) => {
    if (task.is_completed || task.status === 'Completed') {
      return <UilCheckCircle size="18" className="text-success" />;
    }
    const dueDateStatus = task.due_date ? compareDateWithToday(task.due_date) : null;
    if (dueDateStatus === 'Due') {
      return <UilExclamationTriangle size="18" className="text-danger" />;
    }
    return <UilClock size="18" className="text-primary" />;
  };

  const getTaskStats = () => {
    const completed = tasks.filter(t => t.is_completed || t.status === 'Completed').length;
    const pending = tasks.filter(t => !t.is_completed && t.status !== 'Completed').length;
    const overdue = tasks.filter(t => {
      if (t.is_completed || t.status === 'Completed') return false;
      if (!t.due_date) return false;
      return compareDateWithToday(t.due_date) === 'Due';
    }).length;
    
    return { completed, pending, overdue };
  };

  const stats = getTaskStats();

  return (
    <div className="inline-task-list">
      {/* Header */}
      <div className="tasks-header mb-4">
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-3">
          <div className="mb-2 mb-sm-0">
            <h2 className="mb-1 tasks-title" style={{ fontSize: '1.375rem' }}>Tasks</h2>
            <small className="text-muted d-none d-sm-block">Track and manage your tasks</small>
          </div>
          <Button
            variant={showCreateForm ? "secondary" : "primary"}
            onClick={() => {
              setShowCreateForm(!showCreateForm);
              setEditingTaskId(null);
            }}
            className="task-create-btn"
          >
            <FontAwesomeIcon icon={showCreateForm ? faTimes : faPlus} className="me-2" />
            {showCreateForm ? 'Cancel' : 'New Task'}
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="d-flex flex-column flex-sm-row align-items-stretch gap-3 mb-3">
          <SearchBox
            placeholder="Search tasks..."
            className="flex-grow-1"
            style={{ maxWidth: "30rem" }}
            onChange={(e: any) => {
              setSearchQuery(e.target.value);
              onSearchTask(e.target.value);
            }}
            prepend={<FontAwesomeIcon icon={faSearch} className="text-muted" />}
          />
          
          {/* Filter Buttons */}
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant={filterStatus === 'all' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setFilterStatus('all')}
            >
              All ({tasks.length})
            </Button>
            <Button
              variant={filterStatus === 'pending' ? 'warning' : 'outline-warning'}
              size="sm"
              onClick={() => setFilterStatus('pending')}
            >
              Pending ({stats.pending})
            </Button>
            <Button
              variant={filterStatus === 'completed' ? 'success' : 'outline-success'}
              size="sm"
              onClick={() => setFilterStatus('completed')}
            >
              Completed ({stats.completed})
            </Button>
            {stats.overdue > 0 && (
              <Badge bg="danger" className="d-flex align-items-center px-2">
                {stats.overdue} Overdue
              </Badge>
            )}
          </div>
        </div>
        
        <hr className="my-2" />
      </div>

      {/* Inline Create Form */}
      <Collapse in={showCreateForm}>
        <div ref={createFormRef}>
          <Card className="mb-4 create-task-card border-primary">
            <Card.Body>
              <div className="d-flex align-items-start gap-3">
                <div className="task-icon-wrapper">
                  <UilListUl size="24" className="text-primary" />
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-3 text-primary">Create New Task</h6>
                  
                  <Form.Group className="mb-3">
                    <Form.Control
                      ref={newTaskInputRef}
                      type="text"
                      value={newTask.task}
                      onChange={(e) => setNewTask({ ...newTask, task: e.target.value })}
                      placeholder="Enter task description..."
                      className="inline-input"
                    />
                  </Form.Group>
                  
                  <Row className="mb-3">
                    <Col sm={4}>
                      <Form.Group>
                        <Form.Label className="small text-muted mb-1">Priority</Form.Label>
                        <Form.Select
                          value={newTask.priority}
                          onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                          size="sm"
                        >
                          {TaskPriority.map(priority => (
                            <option key={priority} value={priority}>{priority}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col sm={4}>
                      <Form.Group>
                        <Form.Label className="small text-muted mb-1">Status</Form.Label>
                        <Form.Select
                          value={newTask.task_status}
                          onChange={(e) => setNewTask({ ...newTask, task_status: e.target.value })}
                          size="sm"
                        >
                          {TaskStatus.map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col sm={4}>
                      <Form.Group>
                        <Form.Label className="small text-muted mb-1">Due Date</Form.Label>
                        <DatePicker
                          selected={newTask.due_date}
                          onChange={(date: Date | null) => setNewTask({ ...newTask, due_date: date })}
                          dateFormat="MMM dd, yyyy"
                          minDate={new Date()}
                          placeholderText="Select date"
                          className="form-control form-control-sm"
                          isClearable
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      variant="light"
                      size="sm"
                      onClick={cancelCreate}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCreateTask}
                      disabled={!newTask.task.trim() || isSubmitting}
                      className="d-flex align-items-center gap-1"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                      Create Task
                    </Button>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {/* Tasks List */}
      <div className="tasks-feed">
        {filteredTasks.length === 0 ? (
          <Card className="text-center py-5">
            <Card.Body>
              <UilListUl size="48" className="text-muted mb-3" />
              {searchQuery || filterStatus !== 'all' ? (
                <>
                  <h5 className="text-muted">No tasks found</h5>
                  <p className="text-muted mb-4">Try adjusting your filters</p>
                  <Button 
                    variant="outline-primary" 
                    onClick={() => {
                      setSearchQuery('');
                      setFilterStatus('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                </>
              ) : (
                <>
                  <h5 className="text-muted">No tasks yet</h5>
                  <p className="text-muted mb-4">Click "New Task" to create the first task</p>
                </>
              )}
            </Card.Body>
          </Card>
        ) : (
          filteredTasks.map((task) => (
            <Card key={task.id} className={classNames("mb-3 task-card", {
              "editing-task": editingTaskId === task.id,
              "completed-task": task.is_completed || task.status === 'Completed'
            })}>
              <Card.Body className="task-card-body">
                {editingTaskId === task.id ? (
                  // Inline Edit Form
                  <div className="d-flex align-items-start gap-3">
                    <div className="task-icon-wrapper">
                      <UilEdit size="20" className="text-primary" />
                    </div>
                    <div className="flex-grow-1">
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="text"
                          value={editTask.task}
                          onChange={(e) => setEditTask({ ...editTask, task: e.target.value })}
                          className="inline-input"
                          autoFocus
                        />
                      </Form.Group>
                      
                      <Row className="mb-3">
                        <Col sm={4}>
                          <Form.Select
                            value={editTask.priority}
                            onChange={(e) => setEditTask({ ...editTask, priority: e.target.value })}
                            size="sm"
                          >
                            {TaskPriority.map(priority => (
                              <option key={priority} value={priority}>{priority}</option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col sm={4}>
                          <Form.Select
                            value={editTask.task_status}
                            onChange={(e) => setEditTask({ ...editTask, task_status: e.target.value })}
                            size="sm"
                          >
                            {TaskStatus.map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col sm={4}>
                          <DatePicker
                            selected={editTask.due_date}
                            onChange={(date: Date | null) => setEditTask({ ...editTask, due_date: date })}
                            dateFormat="MMM dd, yyyy"
                            minDate={new Date()}
                            placeholderText="Select date"
                            className="form-control form-control-sm"
                            isClearable
                          />
                        </Col>
                      </Row>
                      
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          variant="light"
                          size="sm"
                          onClick={cancelEditing}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleUpdateTask(task.id)}
                          disabled={!editTask.task.trim() || isSubmitting}
                          className="d-flex align-items-center gap-1"
                        >
                          <FontAwesomeIcon icon={faCheck} />
                          Update
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Task Display
                  <div className="d-flex align-items-start gap-3">
                    <div className="task-checkbox">
                      <Form.Check
                        type="checkbox"
                        checked={task.is_completed || task.status === 'Completed'}
                        onChange={() => onToggleTask(task)}
                        className="task-toggle"
                      />
                    </div>
                    
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className={classNames("task-title mb-0", {
                          "text-decoration-line-through text-muted": task.is_completed || task.status === 'Completed'
                        })}>
                          {task.task}
                        </h6>
                        <div className="task-actions">
                          <Button 
                            variant="link" 
                            size="sm" 
                            className="text-decoration-none p-1"
                            onClick={() => startEditingTask(task)}
                            title="Edit task"
                          >
                            <FontAwesomeIcon icon={faPencilAlt} size="sm" />
                          </Button>
                          <Button 
                            variant="link" 
                            size="sm" 
                            className="text-decoration-none text-danger p-1"
                            onClick={() => onDeleteTask(task.id)}
                            title="Delete task"
                          >
                            <UilTrash size="18" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="task-meta d-flex flex-wrap align-items-center gap-3">
                        <div className="d-flex align-items-center gap-1">
                          {getStatusIcon(task)}
                          <span className="small text-muted">
                            {task.is_completed || task.status === 'Completed' ? 'Completed' : task.status}
                          </span>
                        </div>
                        
                        <Badge bg={getPriorityBadge(task.priority)} className="priority-badge">
                          {task.priority} Priority
                        </Badge>
                        
                        {task.due_date && (
                          <div className="d-flex align-items-center gap-1">
                            <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" size="sm" />
                            <span className={classNames("small", {
                              "text-danger": compareDateWithToday(task.due_date) === 'Due' && !task.is_completed,
                              "text-muted": task.is_completed || task.status === 'Completed',
                              "text-warning": compareDateWithToday(task.due_date) === 'Same Date' && !task.is_completed
                            })}>
                              {dateFormatter(task.due_date)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default InlineTaskList;